/**
 * 掲示板カテゴリー (board_categories) の CRUD hooks。
 * 仕様書: docs/02_work/done/plan_board_feature.md §2-1
 *
 * - 一覧読込は Firestore 直接 (read は rules の isInternal で保護)
 * - 作成・改名はクライアント直書き (safeWrite 経由、rules の作成者バインド)
 * - 削除は Callable (deleteBoardCategory: 所属投稿の未分類移動を伴うため)
 */
import type { BoardCategory } from '@goenhub/shared';
import { BOARD_COLLECTIONS } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from '@/hooks/useAuth';
import { db, functions } from '@/lib/firebase';
import { safeAddDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface BoardCategoryRecord extends BoardCategory {
  id: string;
}

// ─────────────────────────────────────────────
// Query
// ─────────────────────────────────────────────

export function useBoardCategories() {
  return useQuery({
    queryKey: ['board_categories'],
    queryFn: async (): Promise<BoardCategoryRecord[]> => {
      const q = query(
        collection(db, BOARD_COLLECTIONS.categories),
        orderBy('order', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as BoardCategory) }));
    },
  });
}

// ─────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────

/** カテゴリー作成（クライアント直書き。order は作成時刻で作成順を維持）。 */
export function useCreateBoardCategory() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string }): Promise<{ id: string }> => {
      if (!user) throw new Error('未認証です');
      const ref = await safeAddDoc(collection(db, BOARD_COLLECTIONS.categories), {
        name: input.name,
        order: Date.now(),
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: ref.id };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_categories'] }),
  });
}

/** カテゴリー改名（owner or 作成者本人。rules 側でも検証される）。 */
export function useRenameBoardCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { categoryId: string; name: string }) => {
      await safeUpdateDoc(doc(db, BOARD_COLLECTIONS.categories, input.categoryId), {
        name: input.name,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_categories'] }),
  });
}

// サーバ (functions/src/board/categories.ts) は { categoryId, movedPosts } を返す
const deleteBoardCategoryFn = httpsCallable<
  { categoryId: string },
  { categoryId: string; movedPosts: number }
>(functions, 'deleteBoardCategory');

/** カテゴリー削除（Callable。所属投稿は「未分類」へ自動移動）。 */
export function useDeleteBoardCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await deleteBoardCategoryFn({ categoryId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board_categories'] });
      // 所属投稿の categoryId が null に一括更新されるため投稿一覧も更新
      qc.invalidateQueries({ queryKey: ['board_posts'] });
    },
  });
}
